'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Footer from '@/app/homepage/components/Footer';

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section = ({ title, children }: SectionProps) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="border-b border-border pb-6 mb-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-left mb-4 group"
      >
        <h2 className="text-2xl font-heading font-bold text-foreground group-hover:text-primary transition-colors">
          {title}
        </h2>
        <Icon
          name="ChevronDownIcon"
          size={24}
          className={`text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && <div className="text-muted-foreground space-y-4">{children}</div>}
    </div>
  );
};

export default function TermsAndConditionsPage() {
  const [lastUpdated, setLastUpdated] = useState('January 4, 2026');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Set dynamic last updated date
    const date = new Date();
    setLastUpdated(date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }));
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const tableOfContents = [
    { id: 'acceptance', title: '1. Acceptance of Terms' },
    { id: 'service-description', title: '2. Service Description' },
    { id: 'user-accounts', title: '3. User Accounts & Responsibilities' },
    { id: 'subscription', title: '4. Subscription & Payment Terms' },
    { id: 'trading-signals', title: '5. Trading Signals & Risk Disclosure' },
    { id: 'intellectual-property', title: '6. Intellectual Property Rights' },
    { id: 'user-conduct', title: '7. User Conduct & Prohibited Activities' },
    { id: 'data-usage', title: '8. Data Usage & Privacy' },
    { id: 'limitation-liability', title: '9. Limitation of Liability' },
    { id: 'termination', title: '10. Termination & Refund Policy' },
    { id: 'modifications', title: '11. Modifications to Terms' },
    { id: 'contact', title: '12. Contact Information' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link href="/homepage" className="flex items-center space-x-2">
              <svg
                width="32"
                height="32"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-primary"
              >
                <path
                  d="M20 4L4 12V28L20 36L36 28V12L20 4Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20 20L28 16M20 20L12 16M20 20V28"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-xl font-heading font-bold">Signaly</span>
            </Link>
            <Link
              href="/homepage"
              className="inline-flex items-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <Icon name="HomeIcon" size={20} className="mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-primary/5 via-background to-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary mb-6">
              <Icon name="DocumentTextIcon" size={20} className="mr-2" />
              <span className="text-sm font-medium">Legal Documentation</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent mb-4">
              Terms and Conditions
            </h1>
            <p className="text-xl text-muted-foreground mb-6">
              Comprehensive terms of service governing your use of Signaly's AI-powered trading platform
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-muted-foreground">
              <div className="flex items-center">
                <Icon name="CalendarIcon" size={16} className="mr-2" />
                Last Updated: {lastUpdated}
              </div>
              <div className="flex items-center">
                <Icon name="CheckCircleIcon" size={16} className="mr-2 text-success" />
                Version 2.0
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Table of Contents - Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              <div className="bg-card rounded-xl p-6 border border-border">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                  Table of Contents
                </h3>
                <nav className="space-y-2">
                  {tableOfContents.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="block w-full text-left px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    >
                      {item.title}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Quick Links */}
              <div className="bg-card rounded-xl p-6 border border-border">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-4">
                  Related Policies
                </h3>
                <div className="space-y-2">
                  <Link
                    href="/privacy-policy"
                    className="flex items-center text-sm text-primary hover:underline"
                  >
                    <Icon name="ShieldCheckIcon" size={16} className="mr-2" />
                    Privacy Policy
                  </Link>
                  <Link
                    href="/contact"
                    className="flex items-center text-sm text-primary hover:underline"
                  >
                    <Icon name="EnvelopeIcon" size={16} className="mr-2" />
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>
          </aside>

          {/* Terms Content */}
          <main className="lg:col-span-3">
            <div className="bg-card rounded-xl p-8 border border-border">
              {/* Search */}
              <div className="mb-8">
                <div className="relative">
                  <Icon
                    name="MagnifyingGlassIcon"
                    size={20}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    type="text"
                    placeholder="Search terms..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground"
                  />
                </div>
              </div>

              {/* Important Notice */}
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-6 mb-8">
                <div className="flex items-start">
                  <Icon name="ExclamationTriangleIcon" size={24} className="text-warning mr-3 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">Important Notice</h3>
                    <p className="text-sm text-muted-foreground">
                      Please read these terms carefully before using Signaly. By accessing or using our services, you
                      agree to be bound by these terms. Trading involves substantial risk of loss and is not suitable
                      for all investors.
                    </p>
                  </div>
                </div>
              </div>

              {/* Terms Sections */}
              <div id="acceptance">
                <Section title="1. Acceptance of Terms">
                  <p>
                    By creating an account, accessing, or using Signaly's platform and services, you acknowledge that
                    you have read, understood, and agree to be bound by these Terms and Conditions, our Privacy Policy,
                    and all applicable laws and regulations.
                  </p>
                  <p>
                    If you do not agree with any part of these terms, you must discontinue use of our services
                    immediately. These terms constitute a legally binding agreement between you and Signaly.
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>You must be at least 18 years old to use our services</li>
                    <li>You must provide accurate and complete registration information</li>
                    <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                    <li>You agree to accept all risks associated with trading activities</li>
                  </ul>
                </Section>
              </div>

              <div id="service-description">
                <Section title="2. Service Description">
                  <p>
                    Signaly provides AI-powered trading signal analysis, market insights, and educational resources.
                    Our platform offers:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Real-time trading signals generated by advanced AI algorithms</li>
                    <li>Market analysis and trend predictions</li>
                    <li>Portfolio tracking and performance analytics</li>
                    <li>Community features including leaderboards and social sharing</li>
                    <li>Educational resources and trading strategies</li>
                    <li>Risk management tools and alerts</li>
                  </ul>
                  <p className="font-semibold mt-4">
                    IMPORTANT: Signaly is an informational service only. We do not provide investment advice, execute
                    trades on your behalf, or manage your funds. All trading decisions are made independently by you.
                  </p>
                </Section>
              </div>

              <div id="user-accounts">
                <Section title="3. User Accounts & Responsibilities">
                  <p>To access premium features, you must create an account with accurate information:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      <strong>Account Security:</strong> You are solely responsible for maintaining the security of
                      your account credentials. Do not share your password with others.
                    </li>
                    <li>
                      <strong>Account Activity:</strong> You are responsible for all activities that occur under your
                      account, whether authorized by you or not.
                    </li>
                    <li>
                      <strong>Unauthorized Access:</strong> Notify us immediately if you suspect unauthorized access to
                      your account.
                    </li>
                    <li>
                      <strong>Account Termination:</strong> We reserve the right to suspend or terminate accounts that
                      violate these terms or engage in fraudulent activity.
                    </li>
                    <li>
                      <strong>Multiple Accounts:</strong> Each user is permitted only one account unless explicitly
                      authorized by Signaly.
                    </li>
                  </ul>
                </Section>
              </div>

              <div id="subscription">
                <Section title="4. Subscription & Payment Terms">
                  <p>Signaly offers various subscription tiers with different features and pricing:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      <strong>Subscription Plans:</strong> Free Basic, Premium ($49/month), and Pro ($99/month) tiers
                      are available
                    </li>
                    <li>
                      <strong>Billing Cycle:</strong> Premium subscriptions are billed monthly on the date you first
                      subscribed
                    </li>
                    <li>
                      <strong>Auto-Renewal:</strong> Subscriptions automatically renew unless cancelled before the
                      renewal date
                    </li>
                    <li>
                      <strong>Payment Methods:</strong> We accept major credit cards, debit cards, and other payment
                      methods via Stripe
                    </li>
                    <li>
                      <strong>Price Changes:</strong> We reserve the right to modify subscription prices with 30 days
                      advance notice
                    </li>
                    <li>
                      <strong>Taxes:</strong> Prices do not include applicable taxes, which will be added at checkout
                    </li>
                    <li>
                      <strong>Failed Payments:</strong> If payment fails, your account may be downgraded or suspended
                      until payment is received
                    </li>
                  </ul>
                </Section>
              </div>

              <div id="trading-signals">
                <Section title="5. Trading Signals & Risk Disclosure">
                  <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-4">
                    <p className="font-semibold text-error">CRITICAL RISK DISCLOSURE:</p>
                  </div>
                  <p>
                    Trading financial instruments involves substantial risk of loss. Past performance does not guarantee
                    future results. You should carefully consider whether trading is appropriate for you based on your
                    financial situation, risk tolerance, and investment objectives.
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mt-4">
                    <li>
                      <strong>No Guarantees:</strong> Our signals and analysis are for informational purposes only and
                      do not guarantee profits
                    </li>
                    <li>
                      <strong>Your Responsibility:</strong> All trading decisions and their outcomes are solely your
                      responsibility
                    </li>
                    <li>
                      <strong>Market Risk:</strong> Financial markets are volatile and can result in partial or total
                      loss of capital
                    </li>
                    <li>
                      <strong>No Financial Advice:</strong> Our services do not constitute financial, investment, or
                      trading advice
                    </li>
                    <li>
                      <strong>AI Limitations:</strong> While our AI is sophisticated, it cannot predict all market
                      movements or guarantee accuracy
                    </li>
                    <li>
                      <strong>Performance Metrics:</strong> Historical performance data is provided for informational
                      purposes only
                    </li>
                  </ul>
                  <p className="mt-4 font-semibold">
                    By using Signaly, you acknowledge that you understand and accept these risks. Consider consulting
                    with a licensed financial advisor before making trading decisions.
                  </p>
                </Section>
              </div>

              <div id="intellectual-property">
                <Section title="6. Intellectual Property Rights">
                  <p>
                    All content, features, and functionality on Signaly are owned by Signaly and are protected by
                    international copyright, trademark, and other intellectual property laws.
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      <strong>Signaly Brand:</strong> The Signaly name, logo, and all related marks are trademarks of
                      Signaly
                    </li>
                    <li>
                      <strong>Platform Content:</strong> All text, graphics, interfaces, code, and data are proprietary
                      to Signaly
                    </li>
                    <li>
                      <strong>AI Algorithms:</strong> Our AI technology and algorithms are proprietary and confidential
                    </li>
                    <li>
                      <strong>Restrictions:</strong> You may not copy, modify, distribute, sell, or reverse engineer any
                      part of our platform
                    </li>
                    <li>
                      <strong>User Content:</strong> You retain rights to content you create, but grant us a license to
                      use it on our platform
                    </li>
                    <li>
                      <strong>Third-Party Content:</strong> Some content may be owned by third parties and subject to
                      their terms
                    </li>
                  </ul>
                </Section>
              </div>

              <div id="user-conduct">
                <Section title="7. User Conduct & Prohibited Activities">
                  <p>You agree not to engage in any of the following prohibited activities:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Violating any applicable laws or regulations</li>
                    <li>Sharing false, misleading, or fraudulent information</li>
                    <li>Attempting to hack, disrupt, or compromise our platform security</li>
                    <li>Using automated tools or bots without authorization</li>
                    <li>Sharing your account credentials with others</li>
                    <li>Manipulating or gaming our systems for unfair advantage</li>
                    <li>Harassing, threatening, or abusing other users or staff</li>
                    <li>Scraping or extracting data from our platform without permission</li>
                    <li>Reselling or redistributing our services without authorization</li>
                    <li>Uploading malicious code or viruses</li>
                  </ul>
                  <p className="mt-4">
                    Violation of these rules may result in immediate account suspension or termination without refund.
                  </p>
                </Section>
              </div>

              <div id="data-usage">
                <Section title="8. Data Usage & Privacy">
                  <p>
                    We collect and process your data to provide and improve our services. Your privacy is important to
                    us:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      <strong>Data Collection:</strong> We collect account information, usage data, and trading
                      preferences
                    </li>
                    <li>
                      <strong>Data Use:</strong> Your data is used to personalize signals, improve our AI, and provide
                      customer support
                    </li>
                    <li>
                      <strong>Data Sharing:</strong> We do not sell your personal data to third parties
                    </li>
                    <li>
                      <strong>Data Security:</strong> We implement industry-standard security measures to protect your
                      data
                    </li>
                    <li>
                      <strong>Cookies:</strong> We use cookies to enhance your experience and analyze platform usage
                    </li>
                    <li>
                      <strong>Your Rights:</strong> You have the right to access, correct, or delete your personal data
                    </li>
                  </ul>
                  <p className="mt-4">
                    For complete details on how we handle your data, please review our{' '}
                    <Link href="/privacy-policy" className="text-primary hover:underline">
                      Privacy Policy
                    </Link>
                    .
                  </p>
                </Section>
              </div>

              <div id="limitation-liability">
                <Section title="9. Limitation of Liability">
                  <p className="font-semibold mb-4">PLEASE READ THIS SECTION CAREFULLY:</p>
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, SIGNALY AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES,
                    AND AGENTS SHALL NOT BE LIABLE FOR:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mt-4">
                    <li>Any trading losses or financial losses resulting from use of our services</li>
                    <li>Inaccurate signals, predictions, or analysis provided by our platform</li>
                    <li>Service interruptions, downtime, or technical failures</li>
                    <li>Loss of data or unauthorized access to your account</li>
                    <li>Third-party actions or services integrated with our platform</li>
                    <li>Any indirect, incidental, consequential, or punitive damages</li>
                  </ul>
                  <p className="mt-4">
                    IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID FOR YOUR SUBSCRIPTION IN THE PAST
                    12 MONTHS.
                  </p>
                  <p className="mt-4">
                    Some jurisdictions do not allow certain limitations of liability, so these limitations may not apply
                    to you.
                  </p>
                </Section>
              </div>

              <div id="termination">
                <Section title="10. Termination & Refund Policy">
                  <p>Both you and Signaly have the right to terminate your account:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      <strong>Your Right to Cancel:</strong> You may cancel your subscription at any time from your
                      account settings
                    </li>
                    <li>
                      <strong>Cancellation Effect:</strong> Cancellation takes effect at the end of your current billing
                      period
                    </li>
                    <li>
                      <strong>Refund Policy:</strong> We offer a 14-day money-back guarantee for first-time subscribers
                    </li>
                    <li>
                      <strong>No Partial Refunds:</strong> We do not provide prorated refunds for mid-cycle
                      cancellations
                    </li>
                    <li>
                      <strong>Our Right to Terminate:</strong> We may suspend or terminate accounts that violate these
                      terms
                    </li>
                    <li>
                      <strong>Effect of Termination:</strong> Upon termination, you lose access to premium features and
                      stored data
                    </li>
                    <li>
                      <strong>Data Retention:</strong> We retain certain data as required by law even after account
                      closure
                    </li>
                  </ul>
                </Section>
              </div>

              <div id="modifications">
                <Section title="11. Modifications to Terms">
                  <p>
                    We reserve the right to modify these Terms and Conditions at any time. Changes will be effective
                    upon posting to our website.
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>
                      <strong>Notification:</strong> We will notify you of significant changes via email or platform
                      notification
                    </li>
                    <li>
                      <strong>Review Requirement:</strong> You are responsible for reviewing terms periodically
                    </li>
                    <li>
                      <strong>Continued Use:</strong> Your continued use after changes constitutes acceptance of new
                      terms
                    </li>
                    <li>
                      <strong>Right to Reject:</strong> If you do not agree with changes, you must discontinue use of
                      our services
                    </li>
                    <li>
                      <strong>Version History:</strong> Previous versions of terms are available upon request
                    </li>
                  </ul>
                </Section>
              </div>

              <div id="contact">
                <Section title="12. Contact Information">
                  <p>For questions, concerns, or support regarding these Terms and Conditions:</p>
                  <div className="bg-muted rounded-lg p-6 mt-4">
                    <h3 className="font-semibold text-foreground mb-4">Signaly Legal Department</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-center">
                        <Icon name="EnvelopeIcon" size={16} className="mr-3 text-primary" />
                        Email: legal@signaly.com
                      </li>
                      <li className="flex items-center">
                        <Icon name="PhoneIcon" size={16} className="mr-3 text-primary" />
                        Phone: +1 (555) 123-4567
                      </li>
                      <li className="flex items-center">
                        <Icon name="MapPinIcon" size={16} className="mr-3 text-primary" />
                        Address: 123 Trading Street, Financial District, NY 10004
                      </li>
                      <li className="flex items-center">
                        <Icon name="ClockIcon" size={16} className="mr-3 text-primary" />
                        Response Time: Within 2-3 business days
                      </li>
                    </ul>
                  </div>
                </Section>
              </div>

              {/* Acceptance Footer */}
              <div className="mt-8 pt-8 border-t border-border">
                <div className="bg-success/10 border border-success/20 rounded-lg p-6">
                  <div className="flex items-start">
                    <Icon name="CheckCircleIcon" size={24} className="text-success mr-3 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">Terms Acceptance</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        By continuing to use Signaly, you acknowledge that you have read, understood, and agree to be
                        bound by these Terms and Conditions.
                      </p>
                      <div className="flex items-center space-x-4">
                        <Link
                          href="/privacy-policy"
                          className="text-sm text-primary hover:underline flex items-center"
                        >
                          <Icon name="ShieldCheckIcon" size={16} className="mr-2" />
                          View Privacy Policy
                        </Link>
                        <Link href="/contact" className="text-sm text-primary hover:underline flex items-center">
                          <Icon name="QuestionMarkCircleIcon" size={16} className="mr-2" />
                          Contact Support
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Print Button */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => window.print()}
                  className="inline-flex items-center px-6 py-3 rounded-lg bg-muted hover:bg-muted/80 text-foreground transition-colors"
                >
                  <Icon name="PrinterIcon" size={20} className="mr-2" />
                  Print or Save as PDF
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}
